// src/store/authSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { LOGIN_MUTATION, REGISTER_MUTATION } from "../../graphql/userMutation";
import client from "../../graphql/client";
import { User } from "../../interfaces/user";
import SecureStorage from "../../utils/secureStorage"; // Custom secure storage

interface AuthResponse {
  token: string;
  refreshToken: string;
  user: User;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  status: "idle" | "loading" | "succeeded" | "failed"; // Fixed type
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  status: "idle",
  error: null,
};

export const loginUser = createAsyncThunk<AuthResponse, { email: string; password: string }, { rejectValue: string }>(
  "auth/loginUser",
  async ({ email, password }, thunkAPI) => {
    try {
      const { data } = await client.mutate({
        mutation: LOGIN_MUTATION,
        variables: { input: { email, password } },
      });
      return data.login;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.message || "Login failed");
    }
  }
);

export const registerUser = createAsyncThunk<AuthResponse, {
  username: string;
  email: string;
  password: string;
  phoneNumber: string;
  role: string;
  gender: string;
  status: string;
}, { rejectValue: string }>(
  "auth/registerUser",
  async (input, thunkAPI) => {
    try {
      const { data } = await client.mutate({
        mutation: REGISTER_MUTATION,
        variables: { input },
      });
      return data.register;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.message || "Signup failed");
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      SecureStorage.clear();
      return { ...initialState };
    },
    setAuthFromStorage: (state) => {
      const user = SecureStorage.get("user");
      const token = SecureStorage.get("token");
      const refreshToken = SecureStorage.get("refreshToken");
      
      if (token && user) {
        state.user = JSON.parse(user);
        state.token = token;
        state.refreshToken = refreshToken;
        state.isAuthenticated = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        const { token, refreshToken, user } = action.payload;
        state.user = user;
        state.token = token;
        state.refreshToken = refreshToken;
        state.isAuthenticated = true;
        state.status = "succeeded";

        // Secure storage instead of localStorage
        SecureStorage.set("user", JSON.stringify(user));
        SecureStorage.set("token", token);
        SecureStorage.set("refreshToken", refreshToken);
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload ?? "Login failed";
      })
      .addCase(registerUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        const { token, refreshToken, user } = action.payload;
        state.user = user;
        state.token = token;
        state.refreshToken = refreshToken;
        state.isAuthenticated = true;
        state.status = "succeeded";

        SecureStorage.set("user", JSON.stringify(user));
        SecureStorage.set("token", token);
        SecureStorage.set("refreshToken", refreshToken);
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload ?? "Signup failed";
      });
  },
});

export const { logout, setAuthFromStorage } = authSlice.actions;
export default authSlice.reducer;